import { resetAPIReducer } from "@reducers/apiReducer/operations";
import { dispatchSetMultipleState } from "@reducers/stateReducer";
import { resetStateReducer } from "@reducers/stateReducer/operations";
import { resetTempReducer } from "@reducers/tempReducer/operations";
import { dispatchSetSnackMsgSuccess } from "@reducers/uiReducer";
import {
	resetUIReducer,
	resetUIReducerWithCache,
} from "@reducers/uiReducer/operations";
import { bookingCycleMgr } from "@services/bookingCycleMgr/index";
import StorageUtils from "@services/storage";
import type { DelayedWritesStore } from "@services/storage/model";
import type { UIKeys } from "@services/storage/storeTemplate";
import type { StateKeys } from "@services/storage/storeTemplate/State";
import { serviceManager } from "@services/workers";
import { logger } from "@services/workers/helpers";
import {
	handleChangesType,
	isGasBoilerServiceJob,
} from "@src/components/Layout/BookingForm/MainForm/Steps/StepThree/RatesTab/DiscountsTab/discountsLogic";
import {
	CARE_PLAN_COMBO_DISCOUNT_ID,
	CARE_PLAN_DISCOUNT_ID,
	VALID_CARE_PLANS,
	VALID_CARE_PLANS_DISCOUNTS,
} from "@src/constants";
import Axios from "axios";
import type { NavigateFunction } from "react-router-dom";
import { cacheUtils, questionsUtils } from "../../index";
import { api, state, temp, ui } from "../../interfaces/interfaces";
import { store } from "../../store";
import { AddressUtils } from "../address";
import type { CheckRestrictions } from "../address/model";
import type { RightioWBSAPIModelsCallsPatchInput } from "../apis/wbs/model";
import type { TradeCode } from "../BasicTypes";
import type { CacheTypes } from "../cache/interfaces";
import { serviceAgreement } from "../objects/index";
import job from "../objects/job";
import ObjectsOperations from "../objects/operations";
import type { ISquareResponse } from "../squareWebSDK/interface";
import type {
	FilterGroupMatchingGroupCode,
	FilterGroupsMatchingTrade,
	FilterJobsMatchingGroup,
	FilterJobsMatchingGroupByDesc,
	FilterJobTypesMatchingGroup,
	FilterTradesMatchingTradeCode,
	FormHandler,
	GetRandomNumber,
	HandleBasicChange,
	HandleBasicChangeWithValue,
	HandleChange,
	HandleGroupChange,
	HandleJobTypeChange,
	HandleJobTypesChange,
	HandleTradeChange,
	Is0InResponse,
	Is3InResponse,
	PostCard,
	PostCustomer,
} from "./interfaces";
import config from "../config";

const namespace = "MAIN SCRIPT";

export const handleChange: HandleChange = (event, value, obj) => {
	value = value ? value : event.target.value ? event.target.value : "";
	const name = event.target.name;
	obj[name] = value;
};

export const getRandomJobNumber: GetRandomNumber = () => {
	return Math.floor(Math.random() * (6620000 - 6000000) + 6000000);
};

export const formHandler: FormHandler = (iFace) => ({
	iFace,
	list: [],
	clear(prefix, excl) {
		this.list.forEach((each) => {
			if (each !== excl && each.startsWith(prefix)) this.iFace[each] = "";
		});
	},
});

export const filterJobsMatchingGroup: FilterJobsMatchingGroup = (
	tradeCode,
	jobGroupCode,
	jobTypes,
) =>
	jobTypes.filter(
		(j) => j.tradeCode === tradeCode && j.jobGroupCode === jobGroupCode,
	);
export const filterJobsMatchingGroupByDesc: FilterJobsMatchingGroupByDesc = (
	tradeCode,
	groupDescription,
	jobTypes,
	groups,
) =>
	jobTypes.filter(
		(j) =>
			j.tradeCode === tradeCode &&
			j.jobGroupCode ===
				groups.filter(
					(g: { groupDescription: string }) =>
						g.groupDescription === groupDescription,
				)[0].jobGroupCode,
	);
export const filterGroupsMatchingTrade: FilterGroupsMatchingTrade = (
	tradeCode,
	tradeGroupsMap,
	activeGroups,
	jobTypes,
) => {
	const filtered = tradeGroupsMap.filter(
		(tGM) => tGM!.tradeCode === tradeCode,
	)[0];

	return filtered.data?.filter(
		(g) =>
			activeGroups.map((aG) => aG.activeGroup).includes(g.groupCode) &&
			jobTypes.some(
				(j: { jobGroupCode: string; tradeCode: TradeCode }) =>
					j.jobGroupCode === g.groupCode && j.tradeCode === tradeCode,
			),
	)!;
}; //.map(t => { return { groupDescription: t.groupDescription } })
export const filterJobTypesMatchingGroup: FilterJobTypesMatchingGroup = (
	jobTypes,
	jobDescription,
) => {
	if (jobTypes)
		return jobTypes.filter(
			(t: { jobDescription: string }) => t.jobDescription === jobDescription,
		)[0];
	return null;
};
export const filterJobTypesMatchingJobTypeCode: FilterJobTypesMatchingGroup = (
	jobTypes,
	jobTypeCode,
) => {
	if (jobTypes)
		return jobTypes.filter(
			(t: { jobTypeCode: string }) => t.jobTypeCode === jobTypeCode,
		)[0];
	return null;
};
export const filterTradesMatchingTradeCode: FilterTradesMatchingTradeCode = (
	tradeCode,
	tradeTypes,
) => {
	if (tradeTypes)
		return tradeTypes.filter(
			(t: { tradeCode: string }) => t.tradeCode === tradeCode,
		)[0];
	return null;
};
export const filterGroupMatchingGroupCode: FilterGroupMatchingGroupCode = (
	jobGroupCode,
	tradeGroups,
) =>
	tradeGroups.filter(
		(g: { jobGroupCode: string }) => g.jobGroupCode === jobGroupCode,
	)[0];

export const is3InResponse: Is3InResponse = (responseTimes, tradeType) =>
	responseTimes[0][tradeType.tradeCode.toLowerCase()] === 3;
export const is0InResponse: Is0InResponse = (responseTimes, tradeType) =>
	responseTimes[0][tradeType.tradeCode.toLowerCase()] === 0;

export const checkRestrictions: CheckRestrictions = (
	tradeType,
	postcodeArea,
	bookingRestrictions,
) =>
	bookingRestrictions.some(
		(r: { postcodeArea: string; tradeCode: TradeCode }) =>
			r.postcodeArea.toLowerCase() === postcodeArea.toLowerCase() &&
			r.tradeCode.toLowerCase() === tradeType.tradeCode.toLowerCase(),
	);

export const dateTimeNow = () =>
	new Date()
		.toISOString()
		.replace(/\.[0-9]{3}/, "")
		.replace("Z", "");
export const zeroPad = (num: number, places: number) =>
	String(num).padStart(places, "0");

export const uppercaseFirstLetter = (word: string) =>
	word.charAt(0).toUpperCase() + word.slice(1);

export const handleBasicChange: HandleBasicChange = (event, prefix) => {
	const value = event.target.value ? event.target.value : "";
	const name = event.target.name;
	ui[(prefix ? prefix + name : name) as UIKeys] = value as never;
};

export const handleBasicChangeWithValue: HandleBasicChangeWithValue = (
	event,
	value,
	name,
	{ ui },
) => {
	value = value ? value : "";
	ui[name as UIKeys] = value as never;
};

export const handleTradeChange: HandleTradeChange = (event, value) => {
	ui.ProblemComboTrade = value;
};

export const handleGroupChange: HandleGroupChange = (event, value) => {
	ui.ProblemComboCategory = value;
};

export const handleJobTypeChange: HandleJobTypeChange = (event, value) => {
	if (value)
		ui.ProblemComboJob = filterJobTypesMatchingGroup(
			ui.ProblemJobs,
			value.jobDescription,
		);
	temp.ProblemAddressPostcodeValid &&
		value &&
		AddressUtils.searchAddressesProblem({ temp, ui, api });
};

export const handleJobTypesChange: HandleJobTypesChange = (event, value) => {
	ui.ProblemComboJobSearch = value;
	temp.ProblemAddressPostcodeValid &&
		value &&
		AddressUtils.searchAddressesProblem({ temp, ui, api });
};

const auth = {
	username: "D3E93B17-958F-4DC7-9F85-0F76AFD72D1C", //FIXME !!! this needs to be fetched from DB or backend
	password: "",
};

export const postCard: PostCard = async ({ body, url, env }) => {
	const postCardUrl = getSetting(`System.CardPayments.URL.${env}`) + url;
	ui.SquareResetEnabled = true;
	Axios.post<ISquareResponse>(postCardUrl, body ? body : {}, {
		auth,
	})
		.then((response) => {
			logger.debug(
				namespace + "->" + postCard.name,
				"Card Posted",
				response.data,
			);
			if (response && response.data && response.data?.Success) {
				ui.SquareCard = response.data;
				ui.BookingCardSecured = true;
				return;
			}
			if (response && response.data && response.data?.Error) {
				ui.SquareError = response.data.Error.Message;
				logger.error(
					namespace + "->" + postCard.name,
					"Card Post Failed",
					response.data.Error.Message,
				);
				return;
			}
			ui.SquareError = "Card data invalid";
			logger.error(
				namespace + "->" + postCard.name,
				"Card Post Failed",
				response.data,
			);
		})
		.catch((error) => {
			const errorMsg = error?.response?.data?.Message
				? error.response.data.Message
				: error.message
					? error.message
					: "Card payment failed";
			ui.SquareError = errorMsg;
			logger.error(
				namespace + "->" + postCard.name,
				"Card Post Failed",
				errorMsg,
			);
		});
};

export const postCustomer: PostCustomer = async ({ body, url, env }) => {
	const postCustomerUrl = getSetting(`System.CardPayments.URL.${env}`) + url;
	Axios.post<ISquareResponse>(postCustomerUrl, body ? { ...body } : {}, {
		auth,
	})
		.then((response) => {
			logger.debug(
				namespace + "->" + postCustomer.name,
				"Customer Posted",
				response.data,
			);
			if (response && response.data && response.data?.Success) {
				ui.SquareCustomerId = response.data.Id;
				return;
			}
			if (response && response.data && response.data?.Error) {
				ui.SquareError = response.data.Error.Message;
				logger.error(
					namespace + "->" + postCustomer.name,
					"Customer Post Failed",
					response.data.Error.Message,
				);
				return;
			}
			ui.SquareError = "Card payment failed - customer data invalid";
			logger.error(
				namespace + "->" + postCustomer.name,
				"Customer Post Failed",
				response.data,
			);
		})
		.catch((error) => {
			logger.error(
				namespace + "->" + postCustomer.name,
				"Customer Post Failed",
				error,
			);
			ui.SquareError = error.message;
		});
};

const getSetting = (key: string) =>
	cacheUtils
		.getCache<CacheTypes.GlobalSettings>("GlobalSettings")
		?.find((s) => s.setting === key)?.settingValue;

export type GetJobScript = ({
	callTypeId,
	isSubPeriod,
	postcodeSector,
	postcodeArea,
}: {
	callTypeId: number;
	isSubPeriod: boolean;
	postcodeSector: string;
	postcodeArea: string;
}) => void;

type MiniJobScript = {
	jobScriptId: number;
	jobScript: string;
	sortOrder: number;
};

type MiniJobScripts = Array<MiniJobScript>;

export const getJobScript: GetJobScript = ({
	callTypeId,
	isSubPeriod,
	postcodeSector,
	postcodeArea,
}) => {
	const { accountCode, accountType } = state.Account!;
	const postcodeAreas =
		cacheUtils.getCache<CacheTypes.PostcodeAreas>("PostcodeAreas");
	const jobScripts = cacheUtils.getCache<CacheTypes.JobScripts>("JobScripts");

	const { JobTradeCode: tradeCode, JobTypeCode: jobTypeCode } = state;

	const areaNumber = postcodeAreas.find(
		(a) => a.postcodeArea === postcodeArea,
	)?.areaNumber;
	state.JobAreaNumber = areaNumber ?? 0;

	const isEqualOrNull = (a: any, b: any) => {
		if (a === b || a === null) return true;
		return false;
	};

	const mapToResult = (a: Partial<MiniJobScripts>, sortOrder: number) =>
		a.map((script) => ({
			jobScriptId: script?.jobScriptId,
			jobScript: script?.jobScript,
			sortOrder,
		})) as MiniJobScripts;

	let results: MiniJobScripts = [];
	const byJobTypeCode = mapToResult(
		jobScripts
			.filter(
				(jS) =>
					jS.jobTypeCode === jobTypeCode &&
					isEqualOrNull(jS.areaNumber, areaNumber) &&
					isEqualOrNull(jS.postcodeSector, postcodeSector) &&
					isEqualOrNull(jS.postcodeArea, postcodeArea) &&
					isEqualOrNull(jS.isSubPeriod, isSubPeriod) &&
					isEqualOrNull(jS.callTypeId, callTypeId),
			)
			.map((jS) => ({
				jobScriptId: jS.jobScriptId,
				jobScript: jS.jobScript,
			})) as Partial<MiniJobScripts>,
		1,
	);

	if (byJobTypeCode.length > 0) results = [...results, ...byJobTypeCode];
	const byTradeCode = mapToResult(
		jobScripts!
			.filter(
				(jS) =>
					jS.tradeCode === tradeCode &&
					isEqualOrNull(jS.areaNumber, areaNumber) &&
					isEqualOrNull(jS.postcodeSector, postcodeSector) &&
					isEqualOrNull(jS.postcodeArea, postcodeArea) &&
					isEqualOrNull(jS.isSubPeriod, isSubPeriod) &&
					isEqualOrNull(jS.callTypeId, callTypeId),
			)
			.map((jS) => ({
				jobScriptId: jS.jobScriptId,
				jobScript: jS.jobScript,
			})) as Partial<MiniJobScripts>,
		2,
	);
	if (byTradeCode.length > 0) results = [...results, ...byTradeCode];
	const byAccountCode = mapToResult(
		jobScripts!
			.filter(
				(jS) =>
					jS.accountCode === accountCode &&
					isEqualOrNull(jS.areaNumber, areaNumber) &&
					isEqualOrNull(jS.postcodeSector, postcodeSector) &&
					isEqualOrNull(jS.postcodeArea, postcodeArea) &&
					isEqualOrNull(jS.isSubPeriod, isSubPeriod) &&
					isEqualOrNull(jS.callTypeId, callTypeId),
			)
			.map((jS) => ({
				jobScriptId: jS.jobScriptId,
				jobScript: jS.jobScript,
			})) as Partial<MiniJobScripts>,
		3,
	);
	if (byAccountCode.length > 0) results = [...results, ...byAccountCode];
	const byAccountType = mapToResult(
		jobScripts!
			.filter(
				(jS) =>
					jS.accountType === accountType &&
					isEqualOrNull(jS.areaNumber, areaNumber) &&
					isEqualOrNull(jS.postcodeSector, postcodeSector) &&
					isEqualOrNull(jS.postcodeArea, postcodeArea) &&
					isEqualOrNull(jS.isSubPeriod, isSubPeriod) &&
					isEqualOrNull(jS.callTypeId, callTypeId),
			)
			.map((jS) => ({
				jobScriptId: jS.jobScriptId,
				jobScript: jS.jobScript,
			})) as Partial<MiniJobScripts>,
		4,
	);
	if (byAccountType.length > 0) results = [...results, ...byAccountType];
	const byAny = mapToResult(
		jobScripts!
			.filter(
				(jS) =>
					isEqualOrNull(jS.tradeCode, tradeCode) &&
					isEqualOrNull(jS.jobTypeCode, jobTypeCode) &&
					isEqualOrNull(jS.accountType, accountType) &&
					isEqualOrNull(jS.areaNumber, areaNumber) &&
					isEqualOrNull(jS.postcodeSector, postcodeSector) &&
					isEqualOrNull(jS.postcodeArea, postcodeArea) &&
					isEqualOrNull(jS.isSubPeriod, isSubPeriod) &&
					isEqualOrNull(jS.callTypeId, callTypeId),
			)
			.map((jS) => ({
				jobScriptId: jS.jobScriptId,
				jobScript: jS.jobScript,
			})) as Partial<MiniJobScripts>,
		5,
	);
	if (byAny.length > 0) results = [...results, ...byAny];

	results = results.sort((a, b) =>
		a.sortOrder > b.sortOrder ? 1 : b.sortOrder > a.sortOrder ? -1 : 0,
	);
	if (results.length === 0) return;
	const { jobScriptId, jobScript } = results[0];

	state.Script = {
		jobScriptId,
		jobScript,
	};
};

export const checkReducedRate = () => {
	if (
		state.ChargeRateReductionDiscountId === 0 || // no rate reduction
		VALID_CARE_PLANS_DISCOUNTS.includes(state.ChargeRateReductionDiscountId) || // valid care plan discount
		state.ChargeReductionAdjustmentRate >=
			state.ChargeReductionMaximumReducedRate // reduced rate is above maximum possible
	)
		return true;
	return false; // reduced rate is below maximum possible
};

export const autoAllocateLookup = async () => {
	const response = await api.fetchAutoAllocation({
		callTypeId: state.CallTypeId || 0,
		jobTypeCode: state.JobTypeCode,
		postcode: state.JobPostcode,
		tradeCode: state.JobTradeCode,
	});
	if (response && response.length > 0) return true;
	return false;
};

export const isToday = (someDate: Date) => {
	const today = new Date();
	return (
		someDate.getDate() === today.getDate() &&
		someDate.getMonth() === today.getMonth() &&
		someDate.getFullYear() === today.getFullYear()
	);
};

export const getTradeType = (tradeCode: TradeCode) =>
	cacheUtils
		.getCache<CacheTypes.TradeTypes>("TradeTypes")
		.find((t) => t.tradeCode === tradeCode);
export const getTradeTypes = () =>
	cacheUtils.getCache<CacheTypes.TradeTypes>("TradeTypes");

export const addDaysToDate = (days: number) => {
	const date = new Date();
	date.setDate(date.getDate() + days);
	return date;
};

const delayedActionsStore: DelayedWritesStore = {};

export const debouncedAction = (
	f: { (): Promise<void>; (): void },
	name: string,
	time: number | undefined,
) => {
	if (delayedActionsStore[name]) {
		clearTimeout(delayedActionsStore[name]);
		delayedActionsStore[name] = setTimeout(() => {
			f();
			clearTimeout(delayedActionsStore[name]);
			delete delayedActionsStore[name];
		}, time) as unknown as number;
		return;
	}
	delayedActionsStore[name] = setTimeout(() => {
		f();
		clearTimeout(delayedActionsStore[name]);
		delete delayedActionsStore[name];
	}, time) as unknown as number;
};

export const isAsProperty = (prefix: string, name: string) => {
	// lowercase name to compare strings
	name = name.toLowerCase();

	// check if form belongs to CustomerCare
	const isCareCustomer = name.includes("carecustomer");

	// check if form belongs to CarePlanDetails
	const isCarePlanDetails = name.includes("careplandetails");

	// check if form belongs to CareBillingDetails
	const isCareBillingDetails = name.includes("carebillingdetails");

	// this will stop revalidating fields when previously validated fields are used
	return (
		prefix.toLowerCase() === "property" &&
		(isCareCustomer || isCarePlanDetails || isCareBillingDetails)
	);
};

export const whichForm = (typeTest: string, whichFormTest: string) => {
	switch (typeTest) {
		case "serviceagreementcustomer":
			if (whichFormTest.includes("contact")) return "CarePlanCustomerContact";
			if (whichFormTest.includes("address")) return "CarePlanCustomerAddress";
			break;
		case "CarePlanPlanDetails":
			if (whichFormTest.includes("address"))
				return "CarePlanPlanDetailsAddress";
			if (whichFormTest.includes("propertytab"))
				return "CarePlanPolicyProperty";
			if (whichFormTest.includes("boiler")) return "CarePlanPolicyBoiler";
			break;
		case "serviceagreementbilling":
			if (whichFormTest.includes("contact")) return "CarePlanBillingContact";
			if (whichFormTest.includes("address")) return "CarePlanBillingAddress";
			if (whichFormTest.includes("billingmethod"))
				return "CarePlanBillingMethod";
			break;
		case "BookingPayment":
			if (whichFormTest.includes("contact")) return "BookingBillingContact";
			if (whichFormTest.includes("address")) return "BookingBillingAddress";
			if (whichFormTest.includes("paymentmethodtab"))
				return "BookingBillingMethod";
			break;
		case "bookingsecuringtab":
			if (whichFormTest.includes("paymentbillingdetails"))
				return "BookingSecuring";
			break;
		case "property":
			if (
				whichFormTest.includes("address") &&
				whichFormTest.includes("paymentbillingdetails")
			)
				return "BookingBillingAddress";
			if (
				whichFormTest.includes("contact") &&
				whichFormTest.includes("paymentbillingdetails")
			)
				return "BookingBillingContact";
			if (
				whichFormTest.includes("address") &&
				whichFormTest.includes("carebillingdetails")
			)
				return "CarePlanBillingAddress";
			if (
				whichFormTest.includes("contact") &&
				whichFormTest.includes("carebillingdetails")
			)
				return "CarePlanBillingContact";
			if (
				whichFormTest.includes("address") &&
				whichFormTest.includes("careplandetails")
			)
				return "CarePlanPlanDetailsAddress";
			if (
				whichFormTest.includes("contact") &&
				whichFormTest.includes("carecustomerdetails")
			)
				return "CarePlanCustomerContact";
			if (
				whichFormTest.includes("address") &&
				whichFormTest.includes("carecustomerdetails")
			)
				return "CarePlanCustomerAddress";
			if (whichFormTest.includes("address")) return "PropertyAddress";
			if (whichFormTest.includes("contact")) return "PropertyContact";
			break;
		case "landlord":
			if (whichFormTest.includes("contact")) return "LandlordContact";
			break;
		case "related":
			if (whichFormTest.includes("contact")) return "RelatedContact";
			break;
		default:
			break;
	}
	return "PropertyAddress";
};

const validateContact = (
	prefix:
		| "LandlordContact"
		| "PropertyContact"
		| "RelatedContact"
		| "CarePlanCustomerContact"
		| "CarePlanBillingContact"
		| "BookingPaymentContact",
) => {
	// use switch for prefix
	switch (prefix) {
		case "RelatedContact":
			if (
				state[`${prefix}TitleValid`] &&
				state[`${prefix}FirstNameValid`] &&
				state[`${prefix}LastNameValid`] &&
				state[`${prefix}RelationshipValid`] &&
				(state[`${prefix}MobileValid`] || state[`${prefix}LandlineValid`])
			) {
				state[`${prefix}Valid`] = true;
			} else {
				state[`${prefix}Valid`] = false;
			}
			validateTab(prefix.replace("Contact", "") as "Related");
			break;

		case "CarePlanCustomerContact":
		case "CarePlanBillingContact":
			if (
				state[`${prefix}TitleValid`] &&
				state[`${prefix}FirstNameValid`] &&
				state[`${prefix}LastNameValid`] &&
				(state[`${prefix}EmailValid`] || !ui[`${prefix}Email`]) &&
				(state[`${prefix}MobileValid`] || state[`${prefix}LandlineValid`])
			) {
				state[`${prefix}Valid`] = true;
			} else {
				state[`${prefix}Valid`] = false;
			}
			validateTab(
				prefix.replace("Contact", "") as "CarePlanCustomer" | "CarePlanBilling",
			);
			break;

		case "LandlordContact":
		case "PropertyContact":
		case "BookingPaymentContact":
			if (
				state[`${prefix}TitleValid`] &&
				state[`${prefix}FirstNameValid`] &&
				state[`${prefix}LastNameValid`] &&
				(state[`${prefix}EmailValid`] || !ui[`${prefix}Email`]) &&
				(state[`${prefix}MobileValid`] || state[`${prefix}LandlineValid`])
			) {
				state[`${prefix}Valid`] = true;
			} else {
				state[`${prefix}Valid`] = false;
			}
			validateTab(
				prefix.replace("Contact", "") as
					| "Landlord"
					| "Property"
					| "BookingPayment",
			);
			break;

		default:
			break;
	}
};

const validateAddress = (
	prefix:
		| "LandlordAddress"
		| "PropertyAddress"
		| "RelatedAddress"
		| "CarePlanCustomerAddress"
		| "CarePlanPlanDetailsAddress"
		| "CarePlanBillingAddress"
		| "BookingPaymentAddress",
) => {
	// use switch for prefix
	const tempPrefix = prefix.replace("Address", "");
	switch (tempPrefix) {
		case "Property":
		case "Related":
			if (
				(state[`${tempPrefix}AddressBusinessNameValid`] ||
					!ui[`${tempPrefix}AddressIsBusiness`]) &&
				state[`${tempPrefix}AddressCityValid`] &&
				state[`${tempPrefix}AddressStreetValid`] &&
				state[`${tempPrefix}AddressPostcodeValid`] &&
				(state[`${tempPrefix}AddressPropertyValid`] ||
					state[`${tempPrefix}AddressPropertyValid`])
			) {
				state[`${tempPrefix}AddressValid`] = true;
			} else {
				state[`${tempPrefix}AddressValid`] = false;
			}
			break;

		case "CarePlanCustomer":
		case "CarePlanPlanDetails":
		case "CarePlanBilling":
			if (
				ui[`${tempPrefix}AddressAsProperty`] ||
				((state[`${tempPrefix}AddressBusinessNameValid`] ||
					!ui[`${tempPrefix}AddressIsBusiness`]) &&
					state[`${tempPrefix}AddressCityValid`] &&
					state[`${tempPrefix}AddressStreetValid`] &&
					state[`${tempPrefix}AddressPostcodeValid`] &&
					(state[`${tempPrefix}AddressPropertyValid`] ||
						state[`${tempPrefix}AddressPropertyValid`]))
			) {
				state[`${tempPrefix}AddressValid`] = true;
			} else {
				state[`${tempPrefix}AddressValid`] = false;
			}
			validateTab(tempPrefix);
			break;

		case "BookingPayment":
			if (
				ui[`${tempPrefix}AddressAsProperty`] ||
				((state[`${tempPrefix}AddressBusinessNameValid`] ||
					!ui[`${tempPrefix}AddressIsBusiness`]) &&
					state[`${tempPrefix}AddressCityValid`] &&
					state[`${tempPrefix}AddressStreetValid`] &&
					state[`${tempPrefix}AddressPostcodeValid`] &&
					(state[`${tempPrefix}AddressPropertyValid`] ||
						state[`${tempPrefix}AddressPropertyValid`]))
			) {
				state[`${tempPrefix}AddressValid`] = true;
			} else {
				state[`${tempPrefix}AddressValid`] = false;
			}
			validateTab(tempPrefix);
			break;

		default:
			break;
	}
};

export const validatePlanDetailsPlanOptionsSideTab = (
	prefix: "CarePlanPlanDetailsPlanOptions",
) => {
	if (
		state[`${prefix}PlanTypeValid`] &&
		state[`${prefix}DurationValid`] &&
		state[`${prefix}BillingFrequencyValid`] &&
		state[`${prefix}ExcessValid`]
	) {
		state[`${prefix}Valid`] = true;
	} else {
		state[`${prefix}Valid`] = false;
	}
	validateTab(prefix.replace("PlanOptions", "") as "CarePlanPlanDetails");
};

export const validateCarePlanPlanDetailsOfferSideTab = (
	prefix: "CarePlanPlanDetailsOffer",
) => {
	if (state[`${prefix}OfferTypeValid`] && state[`${prefix}OfferReasonValid`]) {
		state[`${prefix}Valid`] = true;
	} else {
		state[`${prefix}Valid`] = false;
	}
	validateTab(prefix.replace("Offer", "") as "CarePlanPlanDetails");
};

export const validatePropertySideTab = (
	prefix: "CarePlanPlanDetailsProperty",
) => {
	// prefix without property
	if (
		state[`${prefix}BuildingTypesValid`] &&
		state[`${prefix}BathroomsValid`] &&
		state[`${prefix}BedroomsValid`] &&
		state[`${prefix}ExternalDoorsValid`] &&
		state[`${prefix}GasAppliancesValid`] &&
		state[`${prefix}RadiatorsValid`]
	) {
		state[`${prefix}Valid`] = true;
	} else {
		state[`${prefix}Valid`] = false;
	}
	validateTab(prefix.replace("Property", "") as "CarePlanPlanDetails");
};

export const validateCarePlanPlanDetailsBoilerSideTab = (
	prefix: "CarePlanPlanDetailsBoiler",
) => {
	if (
		state[`${prefix}BoilerModelValid`] &&
		state[`${prefix}BoilerManufacturersValid`]
	) {
		state[`${prefix}Valid`] = true;
	} else {
		state[`${prefix}Valid`] = false;
	}
	validateTab(prefix.replace("Boiler", "") as "CarePlanPlanDetails");
};

export const validateCarePlanBillingMethodSideTab = (
	prefix: "CarePlanBillingMethod",
) => {
	if (state[`${prefix}PaymentTypeValid`]) {
		state[`${prefix}Valid`] = true;
	} else {
		state[`${prefix}Valid`] = false;
	}
	validateTab(prefix.replace("Method", "") as "CarePlanBilling");
};

export const validateCarePlanPaymentBankDetailsSideTab = (
	prefix: "CarePlanPaymentBankDetails",
) => {
	if (
		state[`${prefix}AccountNumberValid`] &&
		state[`${prefix}SortCodeValid`] &&
		state[`${prefix}BankNameValid`]
	) {
		state[`${prefix}Valid`] = true;
	} else {
		state[`${prefix}Valid`] = false;
	}
	validateTab(prefix.replace("BankDetails", "") as "CarePlanPayment");
};

export const validateBookingPaymentTab = (prefix: "BookingPayment") => {
	if (
		(state[`${prefix}MethodPaymentMethodsValid`] &&
			ui[`${prefix}MethodPaymentMethods`]?.id === 8) ||
		ui[`${prefix}MethodCardAgreed`] ||
		(state[`${prefix}MethodCompanyRegistrationAccepted`] &&
			state[`${prefix}MethodPaymentRequisitionsValid`])
	) {
		state[`${prefix}Valid`] = true;
	} else {
		state[`${prefix}Valid`] = false;
	}
	validateTab(prefix);
};

export type ValidateSideTab =
	| "PropertyContact"
	| "LandlordContact"
	| "LandlordAddress"
	| "RelatedContact"
	| "PropertyAddress"
	| "BookingPaymentAddress"
	| "BookingPaymentContact"
	| "CarePlanBillingAddress"
	| "CarePlanBillingContact"
	| "CarePlanBillingMethod" // TODO: remove this
	| "CarePlanCustomerAddress"
	| "CarePlanCustomerContact"
	| "CarePlanPlanDetailsAddress"
	| "CarePlanPaymentBankDetails"
	| "CarePlanPlanDetailsBoiler"
	| "CarePlanPlanDetailsOffer" // TODO: remove this
	| "CarePlanPlanDetailsPlanOptions"
	| "CarePlanPlanDetailsProperty";

export const validateSideTab = (parent: ValidateSideTab) => {
	// use switch on parent
	switch (parent) {
		case "PropertyContact":
		case "LandlordContact":
		case "RelatedContact":
		case "CarePlanCustomerContact":
		case "CarePlanBillingContact":
		case "BookingPaymentContact":
			validateContact(parent);
			break;

		case "BookingPaymentAddress":
		case "CarePlanBillingAddress":
		case "CarePlanCustomerAddress":
		case "CarePlanPlanDetailsAddress":
		case "PropertyAddress":
		case "LandlordAddress":
			validateAddress(parent);
			break;

		case "CarePlanPlanDetailsPlanOptions":
			validatePlanDetailsPlanOptionsSideTab(parent);
			break;

		case "CarePlanPlanDetailsBoiler":
			validateCarePlanPlanDetailsBoilerSideTab(parent);
			break;

		case "CarePlanPlanDetailsProperty":
			validatePropertySideTab(parent);
			break;

		case "CarePlanPlanDetailsOffer":
			validateCarePlanPlanDetailsOfferSideTab(parent);
			break;

		case "CarePlanBillingMethod":
			validateCarePlanBillingMethodSideTab(parent);
			break;

		case "CarePlanPaymentBankDetails":
			validateCarePlanPaymentBankDetailsSideTab(parent);
			break;

		// case "BookingPayment":
		//   validateBookingPaymentTab(parent);
		//   break;

		default:
			break;
	}
};

export type ValidateTab =
	| "BookingPayment"
	| "CarePlanBilling"
	| "CarePlanCustomer"
	| "CarePlanPayment"
	| "CarePlanPlanDetails"
	| "Property"
	| "Related"
	| "Landlord";

export const validateTab = (parent: ValidateTab) => {
	// use switch on parent
	switch (parent) {
		case "CarePlanCustomer":
			if (
				(state[`${parent}ContactValid`] ||
					ui.CarePlanCustomerContactAsContact) &&
				state[`${parent}AddressValid`]
			) {
				state[`${parent}Valid`] = true;
			} else {
				state[`${parent}Valid`] = false;
			}
			carePlanValidate();
			break;

		case "CarePlanBilling":
			if (
				(state[`${parent}ContactValid`] ||
					ui.CarePlanBillingContactAsContact) &&
				state[`${parent}AddressValid`] &&
				state[`${parent}MethodValid`]
			) {
				state[`${parent}Valid`] = true;
			} else {
				state[`${parent}Valid`] = false;
			}
			carePlanValidate();
			break;

		// Plan details is only valid if:
		// 1. Property is valid
		// 2. Address is valid
		// 3. Policy is valid
		// 4. Boiler is valid (if boiler is not required)
		case "CarePlanPlanDetails":
			logger.debug(
				namespace + "->" + validateTab.name,
				"CarePlanPlanDetails",
				JSON.parse(
					JSON.stringify({
						[`${parent}PropertyValid`]: state[`${parent}PropertyValid`],
						[`${parent}AddressValid`]: state[`${parent}AddressValid`],
						[`${parent}PlanOptions`]: state[`${parent}PlanOptionsValid`],
						[`${parent}BoilerValid`]:
							state[
								`${parent.replace("Plan", "Policy")}BoilerValid` as StateKeys
							],
						[`${parent.replace("Plan", "Policy")}BoilerRequired`]:
							ui.CarePlanPlanDetailsIsBoiler,
					}),
				),
			);
			if (
				state[`${parent}PropertyValid`] &&
				state[`${parent}AddressValid`] &&
				state[`${parent}PlanOptionsValid`] &&
				(ui.CarePlanPlanDetailsIsBoiler === false ||
					state[`${parent}BoilerValid` as StateKeys]) &&
				(!ui.CarePlanPlanDetailsOfferOfferType ||
					(!!ui.CarePlanPlanDetailsOfferOfferType &&
						ui.CarePlanPlanDetailsOfferOfferType.serviceAgreementOfferId &&
						ui.CarePlanPlanDetailsOfferOfferReason
							?.serviceAgreementOfferAllocationReasonId))
			) {
				state[`${parent}Valid` as StateKeys] = true as never;
			} else {
				state[`${parent}Valid` as StateKeys] = false as never;
			}

			carePlanValidate();
			break;

		case "CarePlanPayment":
			if (
				state[`${parent}BankDetailsAccountNumberValid`] &&
				state[`${parent}BankDetailsSortCodeValid`] &&
				state[`${parent}BankDetailsBankNameValid`]
			) {
				state[`${parent}Valid`] = true;
			} else {
				state[`${parent}Valid`] = false;
			}
			carePlanValidate();
			break;

		// case "Property":
		//   if (state[`${parent}ContactValid`] && state[`${parent}AddressValid`]) {
		//     state[`${parent}Valid`] = true;
		//   } else {
		//     state[`${parent}Valid`] = false;
		//   }
		//   break;

		// case "Related":
		//   if (state[`${parent}ContactValid`]) {
		//     state[`${parent}Valid`] = true;
		//   } else {
		//     state[`${parent}Valid`] = false;
		//   }
		//   break;

		// case "Landlord":
		//   if (state[`${parent}ContactValid`]) {
		//     state[`${parent}Valid`] = true;
		//   } else {
		//     state[`${parent}Valid`] = false;
		//   }
		//   break;

		case "BookingPayment":
			bookingBillingValid();
			break;

		default:
			break;
	}
};

export const bookingBillingValid = () => {
	if (
		(state.BookingPaymentMethodPaymentMethodsValid &&
			ui.BookingPaymentMethodPaymentMethods?.id === 8) ||
		ui.BookingCardSecured ||
		state.IsAGoBack?.state ||
		state.IsAQuote ||
		state.BookingPaymentMethodCompanyRegistrationAccepted ||
		(ui.BookingRejectionReason?.failedId &&
			ui.BookingRejectionReason.failedId > -1)
	) {
		state.Step3_3Valid = true;
	} else {
		state.Step3_3Valid = false;
	}
};

export const carePlanValidate = () => {
	logger.debug(
		namespace + "->" + carePlanValidate.name,
		"carePlanValidate",
		JSON.parse(
			JSON.stringify({
				[`${state.CarePlanCustomerValid}`]: state.CarePlanCustomerValid,
				[`${state.CarePlanPlanDetailsValid}`]: state.CarePlanPlanDetailsValid,
				[`${state.CarePlanBillingValid}`]: state.CarePlanBillingValid,
			}),
		),
	);
	const isFullyDeclined =
		ui.CarePlanDeclinedReason &&
		ui.CarePlanDeclinedReason.serviceAgreementDeclinedId
			? true
			: false;
	const isServiceAgreementREF = ui.CarePlanReference !== "";

	state.Step3_2Valid =
		isServiceAgreementREF || isFullyDeclined || ui.PropertyAddressIsBusiness;
};

export const fullReset = async (
	navigate: NavigateFunction,
	reason?: string,
) => {
	navigate("/go-home", { replace: true });
	// debounceStorage.remove(`saveToStorage-${state.JobId}`);
	await job.saveIndexedDBOnExit({
		page: "BookingForm",
		action: "fullReset",
		message: "Booking saved",
		reason: reason ? reason : "In Progress Closed",
		booking: state.JobId,
	});
	serviceManager.sendMessageToWorker({
		serviceName: "BOOKING_DATA_SYNC",
		serviceData: {
			command: "FULL_RESET",
			payload: state.JobId,
			origin: "MAIN_THREAD",
		},
	});
	serviceAgreement.reset();
	resetStateReducer([
		"UsersStatistics",
		"UsersStatisticsYesterday",
		"BOOKING_DATA_SYNC",
		"News",
		"UserId",
		"IsAuthenticated",
		"NavMenuCollapsed",
	]);
	resetUIReducer([
		"CallMeBacksQueue",
		"SquareEnableVerifyBuyer",
		"SquareEnableWebSDK",
	]);
	resetTempReducer();
	resetAPIReducer(undefined, store.dispatch, store.getState);
	questionsUtils.reset();
	await StorageUtils.clearIndexedDBStore({
		tableName: "BookingPaymentAddresses",
	});
	await StorageUtils.clearIndexedDBStore({ tableName: "LandlordAddresses" });
	await StorageUtils.clearIndexedDBStore({ tableName: "PropertyAddresses" });
	await StorageUtils.clearIndexedDBStore({
		tableName: "ServiceAgreementBillingAddresses",
	});
	await StorageUtils.clearIndexedDBStore({
		tableName: "ServiceAgreementCustomerAddresses",
	});
	await StorageUtils.clearIndexedDBStore({
		tableName: "ServiceAgreementPolicyAddresses",
	});
	bookingCycleMgr.resetCurrentBooking();
	serviceManager.sendMessageToWorker({
		serviceName: "LOG_SYNC",
		serviceData: {
			command: "SET_JOB_ID",
			payload: -1,
			origin: "MAIN_THREAD",
		},
	});
};

export const redirectToCall = async (
	options: {
		navigate: NavigateFunction;
		callFrom?: number;
		accountCode?: number;
	},
	reason?: string,
) => {
	options.navigate(`switchboard`, {
		replace: true,
		state: {
			newBooking: true,
			callFrom: options.callFrom,
			accountCode: options.accountCode,
		},
	});
	// debounceStorage.remove(`saveToStorage-${state.JobId}`);
	await job.saveIndexedDBOnExit({
		page: "BookingForm",
		action: "callRedirect",
		message: "Booking saved",
		reason: reason ? reason : "In Progress Closed",
		booking: state.JobId,
	});
	serviceManager.sendMessageToWorker({
		serviceName: "BOOKING_DATA_SYNC",
		serviceData: {
			command: "FULL_RESET",
			payload: state.JobId,
			origin: "MAIN_THREAD",
		},
	});
	serviceAgreement.reset();
	resetStateReducer([
		"UsersStatistics",
		"UsersStatisticsYesterday",
		"BOOKING_DATA_SYNC",
		"News",
		"UserId",
		"IsAuthenticated",
		"NavMenuCollapsed",
	]);
	resetUIReducer([
		"CallMeBacksQueue",
		"SquareEnableVerifyBuyer",
		"SquareEnableWebSDK",
	]);
	resetTempReducer();
	resetAPIReducer(undefined, store.dispatch, store.getState);
	questionsUtils.reset();
	await StorageUtils.clearIndexedDBStore({
		tableName: "BookingPaymentAddresses",
	});
	await StorageUtils.clearIndexedDBStore({ tableName: "LandlordAddresses" });
	await StorageUtils.clearIndexedDBStore({ tableName: "PropertyAddresses" });
	await StorageUtils.clearIndexedDBStore({
		tableName: "ServiceAgreementBillingAddresses",
	});
	await StorageUtils.clearIndexedDBStore({
		tableName: "ServiceAgreementCustomerAddresses",
	});
	await StorageUtils.clearIndexedDBStore({
		tableName: "CarePlanPlanDetailsAddresses",
	});
	bookingCycleMgr.resetCurrentBooking();
};

export const newBooking = async (options: {
	navigate: NavigateFunction;
	callFrom?: number;
	accountCode?: number;
}) => {
	if (ui.CallMeBacksModalData?.leadId) {
		api.fetchGetCallMeBackUnlock({
			id: ui.CallMeBacksModalData.leadId,
			objectTypeId: 3,
		});
	}

	// if booking is in progress at the time of call, save it
	options.navigate(`switchboard`, {
		replace: true,
		state: {
			newBooking: true,
			callFrom: options.callFrom,
			accountCode: options.accountCode,
		},
	});
	if (state.JobId) {
		// debounceStorage.remove(`saveToStorage-${state.JobId}`);
		await job.saveIndexedDBOnExit({
			page: "BookingForm",
			action: "fullReset",
			message: "Booking saved",
			reason: "New Booking",
			booking: state.JobId,
		});
	}
	serviceAgreement.reset();
	if (!ui.Done) {
		resetUIReducerWithCache([
			"CallMeBacksQueue",
			"SquareEnableVerifyBuyer",
			"SquareEnableWebSDK",
		]);
	} else
		resetUIReducer([
			"CallMeBacksQueue",
			"SquareEnableVerifyBuyer",
			"SquareEnableWebSDK",
		]);
	resetStateReducer([
		"UsersStatistics",
		"UsersStatisticsYesterday",
		"BOOKING_DATA_SYNC",
		"BookingFormLoaded",
		"CallStarted",
		"FormLocked",
		"News",
		"UserId",
		"IsAuthenticated",
		"NavMenuCollapsed",
	]);
	resetTempReducer();
	resetAPIReducer(undefined, store.dispatch, store.getState);
};

export const appointmentTimeTypeIdOnJob = (appointmentTime: string) => {
	const appointmentTimeTypes =
		cacheUtils.getCache<CacheTypes.AppointmentTimeTypes>(
			"AppointmentTimeTypes",
		);
	if (appointmentTimeTypes && appointmentTimeTypes.length > 0) {
		const appointmentTimeTypeId = appointmentTimeTypes.find(
			(x) => x.name === appointmentTime.toUpperCase(),
		)?.appointmentTimeTypeId;
		if (appointmentTimeTypeId) {
			state.JobAppointmentTimeTypeId = appointmentTimeTypeId ?? 0;
		} else {
			state.JobAppointmentTimeTypeId = 0;
		}
	}
};

export const getFailedReasons = () => {
	const { VALID_STAGE3_FAILED_REASON_IDS } = require("../../constants");
	const reasons = cacheUtils
		.getCache<CacheTypes.FailedReasons>("FailedReasons")
		.filter((x) => VALID_STAGE3_FAILED_REASON_IDS.includes(x.failedId ?? 0));
	return reasons;
};

export const getCurrentVATRate = () => {
	const vatRate = cacheUtils
		.getCache<CacheTypes.VATRates>("VatRates")
		.find((x) => x.isEnabled);
	return vatRate?.rate || 0.2;
};

// return VAT multiplier
export const getVATMultiplier = () => {
	const vatRate = cacheUtils
		.getCache<CacheTypes.VATRates>("VatRates")
		.find((x) => x.isEnabled);
	return vatRate?.rate ? 1 + vatRate?.rate : 1.2;
};

export const calculateVAT = (amount: number) => {
	const vatRate = getCurrentVATRate();
	return amount * vatRate;
};

// calculate with VAT added
export const calculateWithVAT = (amount: number) => {
	return amount + calculateVAT(amount);
};

// calculate with VAT removed
export const calculateWithoutVAT = (amount: number) => {
	const vatRate = getCurrentVATRate();
	return amount / (1 + vatRate);
};

// check if isBusiness
export const isBusiness = () => {
	return state.JobIsCommercial;
};

// calculate the cost with VAT or not depending on isBusiness
export const calculateCostAfterVAT = (amount: number) => {
	return isBusiness() ? amount : calculateWithVAT(amount);
};

// calculate the cost without VAT or not depending on isBusiness
export const calculateCostBeforeVAT = (amount: number) => {
	return isBusiness() ? amount : calculateWithoutVAT(amount);
};

// recursive function to go through object and check if value is a string with date format, replace with date object
export const fixDate = (obj: any) => {
	if (typeof obj === "string" && obj.match(/^\d{4}-\d{2}-\d{2}.*$/)) {
		return new Date(obj);
	}
	if (typeof obj === "object") {
		for (const key in obj) {
			if (
				typeof obj[key] === "string" &&
				obj[key].match(/^\d{4}-\d{2}-\d{2}.*$/)
			) {
				obj[key] = new Date(obj[key]);
			} else if (typeof obj[key] === "object") {
				fixDate(obj[key]);
			}
		}
	}
};

// exportState from state
export const exportReducerState = (storeName: string) => {
	switch (storeName) {
		case "ui":
			return store.getState().ui;
		case "temp":
			return store.getState().temp;
		case "state":
			return store.getState().state;

		default:
			return store.getState().state;
	}
};

// recursive function to go through object values and assign them to new object only if key starts with _, if value is a object, call function again
export const exportObject = (obj: any, force?: boolean) => {
	const newObj: any = {};
	const oldObj = ObjectsOperations.deepCopyObjects(obj);
	//if obj is an object
	for (const [key, value] of Object.entries(oldObj)) {
		if (key.startsWith("_") || force) {
			// if function skip
			if (typeof value === "function") {
				continue;
			}
			if (
				typeof value === "object" &&
				value !== null &&
				!Array.isArray(value) &&
				!(value instanceof Date)
			) {
				newObj[key.slice(1)] = exportObject(value, true);
			} else newObj[key.slice(1)] = value;
		}
	}
	return newObj; // return new object
};

export const isDev = (): boolean =>
	config.debug && config.environment === "development";

export const isTraining = (): boolean =>
	config.debug && config.environment === "training";

// ----------------------------------------------------------------------------
// Helper Functions
// ----------------------------------------------------------------------------

// capitalizeFirstLetter:
// ----------------------------------------------------------------------------
// Capitalizes the first letter of the given string.
// ----------------------------------------------------------------------------
export const capitalizeFirstLetter = (string: string): string => {
	return string.charAt(0).toUpperCase() + string.slice(1);
};

// capitalizeFirstLetter:
// ----------------------------------------------------------------------------
// Capitalizes the first letter of the given string.
// ----------------------------------------------------------------------------
export const getRandomInt = (min: number, max: number): number => {
	min = Math.ceil(min);
	max = Math.floor(max);
	return Math.floor(Math.random() * (max - min + 1)) + min;
};

// deep compare two objects
export const deepCompare = (obj1: any, obj2: any) => {
	// if both are dates
	if (obj1 instanceof Date && obj2 instanceof Date) {
		return obj1.getTime() === obj2.getTime();
	}
	if (typeof obj1 !== "object" || typeof obj2 !== "object") {
		return obj1 === obj2;
	} else if (obj1 === null && obj2 === null) {
		return true;
	} else if (obj1 !== null && obj2 === null) {
		return false;
	} else if (obj1 === null && obj2 !== null) {
		return false;
	} else if (obj1 === undefined && obj2 === undefined) {
		return true;
	} else if (Object.keys(obj1).length !== Object.keys(obj2).length) {
		return false;
	} else if (JSON.stringify(obj1) === JSON.stringify(obj2)) {
		return true;
	}
	for (const key in obj1) {
		if (!Object.hasOwn(obj2, key)) {
			return false;
		}
		if (typeof obj1[key] !== typeof obj2[key]) {
			return false;
		}
		if (typeof obj1[key] === "object") {
			if (!deepCompare(obj1[key], obj2[key])) {
				return false;
			}
		} else if (obj1[key] !== obj2[key]) {
			return false;
		}
	}
	return true;
};

// function to delete all object properties including nested objects and arrays and functions
export const deleteObjectProperties = (obj: any) => {
	for (const key in obj) {
		if (typeof obj[key] === "object") {
			deleteObjectProperties(obj[key]);
		}
		delete obj[key];
	}
};

const getScript = () => {
	logger.debug(namespace + "->" + getScript.name, "called");
	const bIsSubPeriod =
		state.ChargeRateSubRate1 > 0 &&
		// state.ChargeRatePeriod !== state.ChargeRateSubPeriod &&
		state.ChargeRateSubPeriod > 0
			? true
			: false;
	const body = {
		postcodeSector: state.JobPostcodeSector,
		postcodeArea: state.JobPostcodeArea,
		callTypeId: state.CallTypeId || 0,
		isSubPeriod: bIsSubPeriod,
	};
	getJobScript(body);
	logger.debug(namespace + "->" + getScript.name, "finished");
};

export const generateScript = () => {
	const isVATInclusive = false;
	// const isVATInclusive = !isBusiness();
	logger.debug(namespace + "->" + generateScript.name, "called");
	// const { callTypeId, goBackJobId } = job;
	let script = "";
	let charges = "";
	let reducedRateInfo = "";
	let maximumReducedRate = "";
	let lblScript = "";
	// // Disable discounts
	// if (goBackJobId) {
	//   script = "Create a go-back for previous job";
	// } else if (callTypeId === 5) {
	//   script =
	//     "Your claim has now been booked and the details have been passed over for deployment, one of my colleague from that department will call you in the next half hour with an update on your appointment";
	// }
	if (state.ChargePriceAdj > 0) {
		// AND Me.mdblRate = 0
		charges = `Fixed Price £${state.ChargePriceAdj.toFixed(2)}`;
		// charges = `Fixed Price £${calculateCostAfterVAT(state.ChargePriceAdj).toFixed(2)}`;
		if (state.ChargePrice !== state.ChargePriceAdj) {
			reducedRateInfo = `Reduced from £${state.ChargePrice.toFixed(2)}`;
			// reducedRateInfo = `Reduced from £${calculateCostAfterVAT(state.ChargePrice).toFixed(2)}`;
		}
	} else if (state.ChargeRateAdj > 0) {
		let chargesText = `£${state.ChargeRateAdj.toFixed(2)} p/${state.ChargeRateTime}${
			// let chargesText = `£${calculateCostAfterVAT(state.ChargeRateAdj).toFixed(2)} p/${state.ChargeRateTime}${
			isVATInclusive ? "" : " + VAT"
		}`;
		if (
			state.ChargeRateSubRate1 > 0 &&
			state.ChargeRateSubPeriod > 0 &&
			state.ChargeRateSubPeriod !== state.ChargeRatePeriod
		) {
			chargesText += ` then £${state.ChargeRateSubRate1.toFixed(2)} p/${state.ChargeRateSubTime}${
				isVATInclusive ? "" : " + VAT"
			}`;
		}

		charges = chargesText;
		if (state.ChargeRate !== state.ChargeRateAdj) {
			reducedRateInfo = `Reduced from £${state.ChargeRate.toFixed(2)} p/${state.ChargeRateTime}${
				// reducedRateInfo = `Reduced from £${calculateCostAfterVAT(state.ChargeRate).toFixed(2)} p/${state.ChargeRateTime}${
				isVATInclusive ? "" : " + VAT"
			}`;
		} else {
			reducedRateInfo = "";
		}
		maximumReducedRate = `Maximum reduction £${state.ChargeReductionMaximumReducedRate.toFixed(
			// maximumReducedRate = `Maximum reduction £${calculateCostAfterVAT(state.ChargeReductionMaximumReducedRate).toFixed(
			2,
		)}`;
		if (
			state.Account?.accountType === "TradeTek" ||
			state.Account?.accountType === "Online"
		)
			logger.debug(namespace + "->" + generateScript.name, "Discount Enabled");
	} else {
		charges = "Provide Quote";
		script = "We will provide a quote for work to be undertaken";
	}

	lblScript = script;
	logger.debug(namespace + "->" + generateScript.name, "Generate Script", {
		lblCharges: charges,
		lblMaximumReducedRate: maximumReducedRate,
		lblReducedRateInfo: reducedRateInfo,
		lblScript,
	});
	// if (this.Discount.discountId > 0) {
	//   logger.debug(namespace, `Applied - ${this.Discount.discountCode} (${this.Discount.discountDetails})`);
	// } else {
	//   logger.debug(namespace, "No discount applied");
	// }
	getScript();
	logger.debug(namespace + "->" + generateScript.name, "finished");
};

export const fixScript = (script: string) => {
	logger.debug(namespace + "->" + fixScript.name, "called");

	let fixedScript = script;
	fixedScript = fixedScript.replace("£99 INCLUDING VAT", "#INITIALPRICE#");
	fixedScript = fixedScript.replace(
		"ALL in price of £99, so that’s including your parts, labour and VAT ",
		"ALL in price of #INITIALPRICE#, so that’s including your parts and labour ",
	);
	fixedScript = fixedScript.replace(/\t/g, " ");
	return fixedScript;
};

export const processScript = (script: string) => {
	logger.debug(namespace, "processScript Start");
	script = fixScript(script);
	const isVATInclusive = false;
	// const isVATInclusive = !isBusiness();
	script = script.replace(
		/£(\d+\.?(\d+)?)/g,
		`<span class="HARDCODEDRATE"> £${"$1"}</span>`,
	);
	script =
		script.replaceAll("#RATE#", `£${state.ChargeRateAdj.toFixed(2)}`) +
		(isVATInclusive ? "" : " + VAT");
	script = script.replaceAll(
		"#INITIALRATE#",
		`<span class="INITIALRATE"> £${state.ChargeRateAdj.toFixed(2)}${isVATInclusive ? "" : " + VAT"}</span>`,
	);
	script = script.replaceAll(
		"#SUBSRATE#",
		`£${state.ChargeRateSubRate1.toFixed(2)}`,
	);
	script = script.replaceAll(
		"#SUBRATE#",
		`<span class="SUBRATE"> £${state.ChargeRateSubRate1.toFixed(2)}</span>`,
	);
	script =
		script.replaceAll("#PRICE#", `£${state.ChargePriceAdj.toFixed(2)}`) +
		(isVATInclusive ? "" : " + VAT");
	script = script.replaceAll(
		"#INITIALPRICE#",
		`<span class="INITIALPRICE"> £${state.ChargePriceAdj.toFixed(2)}${
			// `<span class="INITIALPRICE"> £${calculateCostAfterVAT(state.ChargePriceAdj).toFixed(2)}${
			isVATInclusive ? "" : " + VAT"
		}</span>`,
	);
	// script = script.replaceAll("#PRICE-INCLUDING-VAT#", `£${(state.ChargePriceAdj * state.VATRate).toFixed(2)}`);
	// script = script.replaceAll("#PRICEINCLUDINGVAT#", `£${(state.ChargePriceAdj * state.VATRate).toFixed(2)}`);
	script = script.replaceAll(
		"#PRICE-INCLUDING-VAT#",
		`£${state.ChargePriceAdj.toFixed(2)}`,
	);
	script = script.replaceAll(
		"#PRICEINCLUDINGVAT#",
		`£${state.ChargePriceAdj.toFixed(2)}`,
	);
	script = script.replaceAll("#SUBSPRICE#", "");
	script = script.replaceAll("#SUBPRICE#", "");
	script = script.replaceAll("#TIME#", state.ChargeRateTime);
	script = script.replaceAll("#TIMELY#", state.ChargeRateTimely);
	script = script.replaceAll("#THETIME#", state.ChargeRateTimely);
	script = script.replaceAll("(Customer name) ", "");
	script = script.replaceAll("[Customer name] ", "");
	script = script.replaceAll("[Customer] ", "");
	script = script.replaceAll(" + VAT + VAT", "");
	// logger.debug(namespace, `processScript Finish ${script}`);
	return script;
};

export const callSubmit = async () => {
	const pureObject: RightioWBSAPIModelsCallsPatchInput = {
		dateTimeCreated: getISODate(state.CallDateTimeCreated),
		userId: state.UserId,
		tradeCode: state.JobTradeCode,
		jobId: state.JobId,
		areaNumber: state.JobAreaNumber,
		booking: state.CallBooking,
		reducedRate: state.CallReducedRate,
		failed: state.CallFailed,
		quote: state.CallQuote,
		goBack: state.CallGoBack,
		other: state.CallOther,
		haukSale: state.CallHAUKSale,
		accountCode: state.Account?.accountCode,
		otherPhoneId: state.CallOtherPhoneId,
		notes: state.CallNotes,
		advancedTypeId: state.JobTimeRequirementTypeId,
	};
	// call has id, so update
	if (state.CallId > 0) {
		// delete jobId in object
		delete pureObject.jobId;
		if (pureObject.notes === "Created by sp_Jobs_InitialBooking")
			pureObject.notes = `Booking ${state.JobId} in progress`;
		const patchedCall = await api.fetchCallsPatch(state.CallId, pureObject); // update
		if (patchedCall?.callId && patchedCall.callId > 0) {
			return true;
		}
		return false; // failed
	}
	return true;
};

export const discountedCharge = (charge: number) => {
	if (state.Discount && state.Discount.discount) {
		const discount = state.Discount.discount;
		const isPercentage = state.Discount.isPercentage;
		const discountAmount = isPercentage ? charge * (discount / 100) : discount;
		return charge - discountAmount;
	}
	return charge;
};

export const inputComponentLogger = (componentName: string, value: any) => {
	logger.debug("INPUT->" + componentName, value);
};

export const getCurrentDiscountAppliedToAmount = (charge: number) => {
	if (!charge) return charge;

	if (state.Discount && state.Discount.discount) {
		state.PreDiscountCharge = charge;
		// check if discount is percentage or fixed
		const isPercentage = state.Discount.isPercentage;
		// calculate discount
		const discountAmount = isPercentage
			? charge * (state.Discount.discount / 100)
			: state.Discount.discount;
		charge = charge - discountAmount;
		// display message
		dispatchSetSnackMsgSuccess({
			ProblemMsg: `Discount ${state.Discount.code} applied`,
		});
		// set promo code applied
		dispatchSetMultipleState({
			PromoCodeApplied: true,
			ChargeTrace:
				state.ChargeTrace +
				` - [Promo ${state.Discount.code}] ${isPercentage ? state.Discount.discount + "%" : "£" + discountAmount}`,
		});
	}
	return charge;
};

export const roundToTwoDecimalPlaces = (num: number | string) => {
	if (typeof num === "string") {
		num = parseFloat(num);
	}
	return Number((Math.round(num * 100) / 100).toFixed(2));
};

export const rateIfCareClubSold = (charge: number) => {
	return `£${roundToTwoDecimalPlaces(charge - charge * 0.1)}`;
};

export const isDateAStr = (date: any) => {
	return typeof date === "string" && date.includes("T");
};

export const isBST = (date: Date): boolean => {
	// Get the year of the input date
	const year: number = date.getFullYear();

	// Create Date objects for the start and end of British Summer Time (BST) in the input year
	const bstStart: Date = new Date(year, 2, 31, 1, 0, 0); // last Sunday of March at 1:00 AM (BST)
	while (bstStart.getDay() !== 0) {
		bstStart.setDate(bstStart.getDate() - 1);
	}

	const bstEnd: Date = new Date(year, 9, 31, 1, 0, 0); // last Sunday of October at 1:00 AM (GMT)
	while (bstEnd.getDay() !== 0) {
		bstEnd.setDate(bstEnd.getDate() - 1);
	}

	// Check if the input date and time is within British Summer Time (BST)
	return date >= bstStart && date < bstEnd;
};

export const getISODate = (date: any) => {
	if (!date) return undefined;
	if (isDateAStr(date)) {
		return date;
	}

	// if date is BST then add 1 hour to the date, this is to fix the issue with BST dates being sent to the server
	if (isBST(date)) {
		const newDate = new Date(date);
		newDate.setHours(newDate.getHours() + 1);
		return newDate.toISOString();
	}

	return date.toISOString();
};

export const applyCCDiscount = (carePlanReference: string) => {
	const isCCComboEligible = VALID_CARE_PLANS.includes(
		ui.CarePlanPlanDetailsPlanOptionsPlanType?.serviceAgreementTypeId || 0,
	);
	const is18months =
		ui.CarePlanPlanDetailsPlanOptionsDuration?.serviceAgreementDurationId ===
		18;
	const isComboEligible =
		isCCComboEligible && is18months && isGasBoilerServiceJob(state.JobTypeCode);
	const targetDiscount = isComboEligible
		? CARE_PLAN_COMBO_DISCOUNT_ID
		: CARE_PLAN_DISCOUNT_ID;
	const targetDiscountObject =
		cacheUtils
			.getCache<CacheTypes.DiscountTypes>("DiscountTypes")
			.find((x) => x.discountId === targetDiscount) || null;
	console.log(
		"CarePlanReference",
		carePlanReference,
		isComboEligible,
		targetDiscount,
		targetDiscountObject,
	);
	if (carePlanReference && carePlanReference.length > 0) {
		state.JobHaveOfferedServiceAgreement = true;
		state.JobServiceAgreementDeclinedId = 9;
		// auto CC reduction
		handleChangesType(null, targetDiscountObject);
	}
};
